import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Home, Building2, FileText, ShoppingBag, Users, QrCode, Settings, LogOut } from "lucide-react";
import { toast } from "sonner";

const items = [
  { to: "/",           label: "Home",       Icon: Home,       testid: "sidebar-home" },
  { to: "/businesses", label: "Businesses", Icon: Building2,  testid: "sidebar-businesses" },
  { to: "/enquiries",  label: "Enquiries",  Icon: FileText,   testid: "sidebar-enquiries" },
  { to: "/orders",     label: "Orders",     Icon: ShoppingBag,testid: "sidebar-orders" },
  { to: "/employees",  label: "Employees",  Icon: Users,      testid: "sidebar-employees" },
  { to: "/qr",         label: "QR Library", Icon: QrCode,     testid: "sidebar-qr" },
  { to: "/portfolio",  label: "Portfolio",  Icon: Settings,   testid: "sidebar-portfolio" },
];

/**
 * Desktop sidebar navigation — visible only at `lg:` breakpoint.
 * Mounted globally alongside the mobile BottomNav; each is shown at the right
 * breakpoint via Tailwind visibility classes.
 */
export function DesktopSidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Owner-only nav — MobileShell already skips mounting this for employees,
  // but guard here too in case it's ever rendered outside that context.
  if (user?.role === "employee") return null;
  const initial = (user?.name || "?").charAt(0).toUpperCase();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Signed out");
    } catch (_e) {
      // logout() always redirects — nothing meaningful to surface here.
    }
  };

  return (
    <aside
      data-testid="desktop-sidebar"
      className="hidden lg:flex fixed left-0 top-0 h-screen w-56 bg-white border-r border-slate-200 flex-col py-6 z-40"
    >
      <div className="px-5 mb-8 flex items-center gap-2">
        <div className="w-10 h-10 rounded-2xl bg-emerald-600 text-white grid place-items-center shadow-lg shadow-emerald-500/20">
          <span className="font-bold text-lg">K</span>
        </div>
        <div>
          <div className="font-bold text-slate-900 leading-none">KonexoCRM</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">Business Connected</div>
        </div>
      </div>
      <nav className="flex-1 px-3 space-y-1">
        {items.map(({ to, label, Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={testid}
            className={({ isActive }) =>
              `flex items-center gap-3 h-11 rounded-xl px-3 text-sm font-semibold transition ${
                isActive
                  ? "bg-emerald-50 text-emerald-700"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`
            }
          >
            <Icon className="w-4 h-4" strokeWidth={2.2} />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Account + logout footer */}
      {user && (
        <div className="px-3 pt-3 border-t border-slate-100 mt-2 space-y-2" data-testid="sidebar-account">
          <button
            onClick={() => navigate("/settings")}
            data-testid="sidebar-account-btn"
            className="w-full flex items-center gap-3 h-11 rounded-xl px-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition"
          >
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-100 to-indigo-200 text-indigo-700 grid place-items-center text-xs font-bold shrink-0">
              {initial}
            </div>
            <div className="min-w-0 flex-1 text-left">
              <div className="truncate text-[13px]">{user.name || user.email}</div>
              <div className="truncate text-[10px] text-slate-500 font-normal">{user.business_name || user.email}</div>
            </div>
          </button>
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout-btn"
            className="w-full flex items-center gap-3 h-11 rounded-xl px-3 text-sm font-semibold text-red-600 hover:bg-red-50 transition"
          >
            <LogOut className="w-4 h-4" strokeWidth={2.2} />
            Log out
          </button>
        </div>
      )}
    </aside>
  );
}
